import React from "react";
import { Layout, Menu, Typography, message } from "antd";
import CoursesTable from "./CoursesTable";
import CourseForm from "./CourseForm";
import CourseLessons from "./CourseLessons";
import UsersTable from "./UsersTable";
import UserForm from "./UserForm";
import InvitesTable from "./InvitesTable";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import LoginPage from "./LoginPage";
import { BookOutlined, TeamOutlined, GiftOutlined } from "@ant-design/icons";
import { getCurrentUser } from "./api";

const { Header, Content, Sider } = Layout;
const { Title } = Typography;
const queryClient = new QueryClient();

export default function AdminApp() {
  const [isAuth, setIsAuth] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [showForm, setShowForm] = React.useState(false);
  const [editingCourseId, setEditingCourseId] = React.useState<number | undefined>();
  const [viewingLessons, setViewingLessons] = React.useState<number | undefined>();
  const [currentView, setCurrentView] = React.useState<'courses' | 'users' | 'invites'>('courses');
  const [editingUserId, setEditingUserId] = React.useState<number | undefined>();

  React.useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem("jwt");
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      await getCurrentUser();
      setIsAuth(true);
    } catch (error: any) {
      localStorage.removeItem("jwt");
      message.error(error.message || "Ошибка авторизации");
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        minHeight: "100vh", 
        display: "flex", 
        alignItems: "center", 
        justifyContent: "center", 
        background: "#f0f2f5" 
      }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: 24, marginBottom: 16 }}>Загрузка...</div>
        </div>
      </div>
    );
  }

  if (!isAuth) {
    return <LoginPage onLogin={() => setIsAuth(true)} />;
  }

  const handleEditCourse = (courseId: number) => {
    setEditingCourseId(courseId);
    setShowForm(true);
    setViewingLessons(undefined);
    setCurrentView('courses');
  };

  const handleCreateCourse = () => {
    setEditingCourseId(undefined);
    setShowForm(true);
    setViewingLessons(undefined);
    setCurrentView('courses');
  };

  const handleViewLessons = (courseId: number) => {
    setViewingLessons(courseId);
    setShowForm(false);
    setEditingCourseId(undefined);
    setCurrentView('courses');
  };

  const handleEditUser = (userId: number) => {
    setEditingUserId(userId);
    setCurrentView('users');
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingCourseId(undefined);
    setEditingUserId(undefined);
    // Обновляем данные в таблице
    if (currentView === 'courses') {
      queryClient.invalidateQueries({ queryKey: ['courses'] });
    } else if (currentView === 'users') {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    } else if (currentView === 'invites') {
      queryClient.invalidateQueries({ queryKey: ['invites'] });
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingCourseId(undefined);
    setViewingLessons(undefined);
    setEditingUserId(undefined);
  };

  const handleBackToCourses = () => {
    setViewingLessons(undefined);
    setCurrentView('courses');
  };

  const handleMenuClick = (key: string) => {
    setCurrentView(key as 'courses' | 'users' | 'invites');
    setShowForm(false);
    setEditingCourseId(undefined);
    setViewingLessons(undefined);
    setEditingUserId(undefined);
  };

  const renderContent = () => {
    if (showForm) {
      return (
        <CourseForm
          courseId={editingCourseId}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      );
    }

    if (viewingLessons) {
      return (
        <CourseLessons
          courseId={viewingLessons}
          onBack={handleBackToCourses}
        />
      );
    }

    if (editingUserId) {
      return (
        <UserForm
          userId={editingUserId}
          onSuccess={handleFormSuccess}
          onCancel={handleFormCancel}
        />
      );
    }

    if (currentView === 'users') {
      return (
        <UsersTable onEditUser={handleEditUser} />
      );
    }

    if (currentView === 'invites') {
      return (
        <InvitesTable />
      );
    }

    return (
      <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <Title level={3} style={{ margin: 0, color: "#1976d2" }}>
            Курсы
          </Title>
          <button
            onClick={handleCreateCourse}
            style={{
              background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 20px",
              fontWeight: 600,
              fontSize: 15,
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
              transition: "all 0.2s"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(25, 118, 210, 0.4)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(25, 118, 210, 0.3)";
            }}
          >
            + Создать курс
          </button>
        </div>
        <CoursesTable 
          onEditCourse={handleEditCourse} 
          onViewLessons={handleViewLessons}
        />
      </>
    );
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Layout style={{ minHeight: "100vh", background: "#f4f6fa" }}>
        <Sider
          width={220}
          style={{
            background: "linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)",
            boxShadow: "2px 0 8px 0 rgba(25, 118, 210, 0.08)",
          }}
        >
          <div style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 22,
            color: "#fff",
            letterSpacing: 1,
            marginBottom: 12,
            borderBottom: "1px solid #ffffff22"
          }}>
            <BookOutlined style={{ fontSize: 28, marginRight: 12 }} />
            LMS Admin
          </div>
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[currentView]}
            style={{ background: "transparent", fontSize: 17, fontWeight: 500 }}
            onClick={({ key }) => handleMenuClick(key)}
          >
            <Menu.Item key="courses" icon={<BookOutlined />}>Курсы</Menu.Item>
            <Menu.Item key="users" icon={<TeamOutlined />}>Пользователи</Menu.Item>
            <Menu.Item key="invites" icon={<GiftOutlined />}>Инвайты</Menu.Item>
          </Menu>
        </Sider>
        <Layout>
          <Header
            style={{
              background: "linear-gradient(90deg, #1976d2 0%, #42a5f5 100%)",
              color: "#fff",
              fontWeight: 700,
              fontSize: 22,
              boxShadow: "0 2px 8px 0 rgba(25, 118, 210, 0.08)",
              padding: "0 32px",
              display: "flex",
              alignItems: "center",
              minHeight: 64,
            }}
          >
            <span style={{ flex: 1 }}>Панель администратора</span>
            <button
              onClick={() => {
                localStorage.removeItem("jwt");
                setIsAuth(false);
              }}
              style={{
                background: "#fff",
                color: "#1976d2",
                border: "none",
                borderRadius: 6,
                padding: "7px 18px",
                fontWeight: 600,
                fontSize: 15,
                cursor: "pointer",
                boxShadow: "0 2px 8px 0 rgba(25, 118, 210, 0.08)",
                transition: "background 0.2s"
              }}
            >
              Выйти
            </button>
          </Header>
          <Content
            style={{
              margin: "32px 24px 0",
              padding: 32,
              background: "#fff",
              borderRadius: 16,
              minHeight: 360,
              boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            }}
          >
            {renderContent()}
          </Content>
        </Layout>
      </Layout>
    </QueryClientProvider>
  );
} 